import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from 'react-router';
import { ArtworkStub, ArtworkNavigationState } from "../models/Artwork";
import {
    Alert, Select, MenuItem, FormControl, Box, useMediaQuery,
    useTheme, Button, Typography, Tooltip, Stack,
    InputLabel,
} from '@mui/material';
import '../App.css';
import { useUser } from '../components/UserContext';
import SectionLabel from '../components/SectionLabel';
import { createApiClient } from "../components/useApiClient";
import { useSafeRequest } from "../components/useSafeRequest";
import { SearchData, buildCurrentFilterText, buildCurrentSearchSummary, PresenceFilter } from '../models/SearchElements';
import { useCurrency } from '../components/CurrencyContext';
import CurrencyTickerTape from "../components/CurrencyTickerTape";
import { useReferenceData } from '../components/ReferenceDataContext';
import type { AppCategory } from "../models/Category";
import { PortfolioDto } from "../models/Portfolio";
import { useFilter } from '../components/Filter/ArtworkFilterContext';
import PortfolioValuation from "../components/Valuations/PortfolioValuation";
import { convertArtworkDetailsToBaseCurrency, ArtworkBaseValuation } from "../models/Valuation";
import { ValuationHeaderData } from "../models/Valuation";
import ArtworkHoverPreview from "../components/ArtworkPreview/ArtworkHoverPreview";
import ArtworkStatusBadge from "../components/ArtworkPreview/ArtworkStatusBadge";
import ArtworkTableView from "../components/ArtworkDetails/ArtworkTableView";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExcelJS from "exceljs";
import { getArtworkStatusInfo } from "../functions/ArtworkStatus";
import QuickFilterMenu from "../components/MenuBar/QuickFilterMenu";
//Locations
import type { Location } from '../models/Locations';
import { buildLocationIndexes } from '../models/Locations';
import type { InputLocation } from "../models/AppLocation";
//Filter Bar
import { ActiveFiltersBar } from "../components/Filter/ActiveFiltersBar";
import type { Artist } from '../models/artist';
//Reporting
import { ArtworkReportRequest, ReportSelectionRequest, buildArtworkReportRequest } from "../models/Reporting";
import ReportSelectionDialog from "../components/Reporting/ReportSelectionDialog";
import AssessmentIcon from "@mui/icons-material/Assessment";
import type { ValuationType } from "../models/SearchElements";
//Saved Artwork Views
import type { SavedArtworkViewListItemDto, LoadSavedArtworkViewResultDto } from "../models/SaveArtworkViewRequests";
import { useArtworkViewsApi } from "../functions/SavedArtworkViewsApi";
import SaveArtworkViewDialog from "../components/SavedArtworkViews/SaveArtworkViewDialog";
import SavedArtworkViewsDialog from "../components/SavedArtworkViews/SavedArtworkViewsDialog";
//Collection Switcher
import CollectionSwitcher from "../components/Collection/CollectionSwitcher";
import { useApiClient } from "../components/useApiClient";
type GalleryPageProps = {
    refreshTrigger?: boolean;
};
const GalleryPage: React.FC<GalleryPageProps> = ({ refreshTrigger }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const HoverMetaRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
        if (!value) return null;
        return (
            <div className="hover-preview-meta-row">
                <span className="hover-preview-meta-label">{label}</span>
                <span className="hover-preview-meta-value">{value}</span>
            </div>
        );
    };
    const { categories, locations } = useReferenceData();
    const { locationLookup, nodeLookup } = buildLocationIndexes(locations);
    const categoryMap = new Map<number, string>(
        categories?.map(cat => [cat.id, cat.name])
    );
    const getCategoryNameById = (id: number): string | undefined => {
        return categoryMap.get(id);
    };
    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, isViewOnly, isFinance, user, hasClientLevelRole, setActiveClientId,
        compactView, showValues, dimensionDisplayPreference, dimensionUnitPreference    } = useUser();
    const canEditArtwork = isAdmin || isOwner || isValuer;
    const hasFinanceRole = (isAdmin || isOwner || isFinance);
    const { postJsonBlob } = useApiClient();
    const { currency } = useCurrency();
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);
    const [portfolios, setPortfolios] = useState<PortfolioDto[]>([]);
    const [artists, setArtists] = useState<Artist[]>([]);
    const [categoriesData, setCategoriesData] = useState<AppCategory[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [valuationData, setValuationData] = useState<ValuationHeaderData[] | null>(null);
    const [baseValuationData, setBaseValuationData] = useState<ArtworkBaseValuation[] | null>(null);
    const [sortBy, setSortBy] = useState<string>("artist");
    const [conversionRates, setConversionRates] = useState<Record<string, number>>({});
    const [baseCurrency, setBaseCurrency] = useState("USD");
    const [totalValue, setTotalValue] = useState(0);
    const [valueViewOption, setValueViewOption] = useState<ValuationType>("acquisition");
    // Gallery display mode (card grid vs table/list) — remembered per browser via localStorage.
    const GALLERY_VIEW_MODE_KEY = "amh.gallery.viewMode";
    const [viewMode, setViewModeState] = useState<"card" | "table">(() => {
        if (typeof window === "undefined") return "card";
        return window.localStorage.getItem(GALLERY_VIEW_MODE_KEY) === "table" ? "table" : "card";
    });
    const setViewMode = (mode: "card" | "table" | null) => {
        if (!mode) return; // ToggleButtonGroup reports null when the active button is clicked again
        setViewModeState(mode);
        try {
            window.localStorage.setItem(GALLERY_VIEW_MODE_KEY, mode);
        } catch {
            // localStorage unavailable (e.g. privacy mode) — view choice just won't persist
        }
    };
    // Maps a table column to the ascending/descending sortBy keys it toggles between.
    const COLUMN_SORT_KEYS: Record<string, { asc: string; desc?: string }> = {
        artist: { asc: "artist", desc: "artist_desc" },
        title: { asc: "title", desc: "title_desc" },
        category: { asc: "category", desc: "category_desc" },
        location: { asc: "location", desc: "location_desc" },
        dateAcquired: { asc: "acquired_asc", desc: "acquired_desc" },
        value: { asc: "value_asc", desc: "value_desc" },
    };
    const handleColumnSort = (columnKey: string) => {
        const keys = COLUMN_SORT_KEYS[columnKey];
        if (!keys) return;
        if (!keys.desc) {
            setSortBy(keys.asc);
            return;
        }
        setSortBy(sortBy === keys.asc ? keys.desc : keys.asc);
    };
    const { filterParams, filteredArtworks, artworks, setFilterParams, setArtworks, setFilteredArtworks, resetFilter } = useFilter();
    const handleFilterChange = (field: keyof SearchData, value: any) => {
        setFilterParams({ ...filterParams, [field]: value });
    };
    const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
    const handlePortfolioChange = (id: number) => {
        resetFilter(clientIdRef.current ?? 0);
        handleFilterChange("portfolioId", id === 0 ? null : id);
        setSelectedPortfolioId(id === 0 ? null : id);
    }
    const buildLocationOptions = (locations: any[], parentId: number | null = null, level = 0): any[] => {
        return locations
            .filter((l) => l.parentLocationId === parentId)
            .flatMap((l) => [
                { id: l.locationId, name: `${"— ".repeat(level)}${l.name}` },
                ...buildLocationOptions(locations, l.locationId, level + 1)
            ]);
    };
    const currentClient = user?.clients?.find((c) => c.clientId === activeClientId) ?? null;
    const defaultPortfolioId = currentClient?.defaultPortfolioId ?? null;
    const effectivePortfolioId = useMemo(() => {
        if (selectedPortfolioId != null && selectedPortfolioId > 0)
            return selectedPortfolioId;
        if (defaultPortfolioId != null && defaultPortfolioId > 0)
            return defaultPortfolioId;
        return null;
    }, [activeClientId, currentClient, selectedPortfolioId]);
    //Reporting
    const [generatingReport, setGeneratingReport] = useState(false);
    const [showReportDialog, setShowReportDialog] = React.useState(false);

    function downloadBlobFile(blob: Blob, fileName: string) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
    async function generateArtworkReport(
        request: ArtworkReportRequest,
        accessToken: string
    ): Promise<Blob> {
        return await postJsonBlob<Blob>(
            "reports/GenerateArtworkReport",
            accessToken,
            request
        );
    }
    function buildArtworkReportFileName(request: ArtworkReportRequest): string {
        const safeTitle = (request.title || "artwork-report")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        const type = request.reportType || "catalogue";
        const date = new Date().toISOString().slice(0, 10);
        const extension = request.reportFormat === "Excel" ? "xlsx" : "pdf";
        return `${safeTitle}-${type}-${date}.${extension}`;
    }
    const currentFilterText = buildCurrentFilterText(filterParams, artists, categoriesData, locations, portfolios);
    const filterSummary = buildCurrentSearchSummary(
        filterParams,
        artists,
        categoriesData,
        locationLookup,
        portfolios,
        filteredArtworks.length
    );
    const handleReportPreparation = () => {
        setShowReportDialog(true);
    }
    const handleReportDialogClose = () => {
        setShowReportDialog(false);
    }
    const handleGenerateReport = async (options: ReportSelectionRequest) => {
        try {
            setGeneratingReport(true);
            const accessToken = await getAccessToken();
            if (accessToken) {
                const request = buildArtworkReportRequest(
                    filterParams,
                    options,
                    currency
                );
                const blob = await generateArtworkReport(request, accessToken);
                downloadBlobFile(blob, buildArtworkReportFileName(request));
            }
            setShowReportDialog(false);
        } catch (error) {
            setError("Failed to export catalogue");
            console.error("Failed to generate report", error);

        } finally {
            setGeneratingReport(false);
        }
    };
    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );
    const { get } = useApiClient();
    const exchangeRateReq = useSafeRequest<Record<string, number>>(api, { maxAttempts: 3 });
    const artworkReq = useSafeRequest<ArtworkStub[]>(api, { maxAttempts: 3 });
    const portfolioReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const valuationsReq = useSafeRequest<ValuationHeaderData[]>(api, { maxAttempts: 3 });
    const artistsReq = useSafeRequest<Artist[]>(api, { maxAttempts: 3 });
    const locationsReq = useSafeRequest<Location[]>(api, { maxAttempts: 3 });
    const [hoverArtwork, setHoverArtwork] = useState<ArtworkStub | null>(null);
    const [hoverPosition, setHoverPosition] = useState<{ x: number, y: number } | null>(null);
    const HOVER_CARD_WIDTH = 260;
    const HOVER_CARD_GAP = 12;
    const HOVER_CARD_HEIGHT_ESTIMATE = 320;
    const refresh = async (usePortId: number | null) => {
        const artworksUrl =
            usePortId != null
                ? `artworks?portfolioId=${usePortId}`
                : `artworks`;
        const valuationsUrl =
            usePortId != null
                ? `valuations/portfoliodetail/${usePortId}`
                : `valuations/clientdetail/${clientIdRef.current}`;
        const tasks = [
            exchangeRateReq.run({ method: "GET", url: `exchangerates/${currency}` }).then(d => setConversionRates(d)).catch(() => { }),
            //artworkReq.run({ method: "GET", url: `${artworksUrl}` }).then(d => { setArtworks(d); }).catch(() => { }),
            portfolioReq.run({ method: "GET", url: `portfolios` }).then(p => setPortfolios(p)).catch(() => { }),
            valuationsReq.run({ method: "GET", url: `${valuationsUrl}` }).then(v => setValuationData(v)).catch(() => { }),
            artistsReq.run({ method: "GET", url: `artists/for-client/${activeClientId}` }).then(a => setArtists(a)).catch(() => { }),
        ];
        await Promise.all(tasks);

        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token');

        const res = await get<ArtworkStub[]>(`${artworksUrl}`, accessToken,);
        setArtworks(res);


    };

    function getPathLabels(id: number, parent: Map<number, number | null>, labels: Map<number, string>): string[] {
        const out: string[] = [];
        let cur: number | null | undefined = id;
        const guard = new Set<number>(); // avoid cycles
        while (cur != null && !guard.has(cur)) {
            guard.add(cur);
            out.unshift(labels.get(cur) ?? String(cur));
            cur = parent.get(cur) ?? null;
        }
        return out;
    };
    function matchesDateFilter(
        acquired: string | null | undefined,
        from?: string | null,
        to?: string | null
    ): boolean {
        // If no date filters → always include
        if (!from && !to) return true;
        // If filters exist but date is null → exclude
        if (!acquired) return false;
        // Compare ISO strings safely
        if (from && acquired < from) return false;
        if (to && acquired > to) return false;
        return true;
    }
    function makeParentIndex(flat: AppCategory[]) {
        const parent = new Map<number, number | null>();
        const label = new Map<number, string>();
        for (const c of flat) {
            parent.set(c.id, c.parentId ?? null);
            label.set(c.id, c.name);
        }
        return { parent, label };
    };
    const { parentIndex, labelIndex } = React.useMemo(() => {
        if (!categories?.length) return { parentIndex: new Map<number, number | null>(), labelIndex: new Map<number, string>() };
        const idx = makeParentIndex(categories);
        return { parentIndex: idx.parent, labelIndex: idx.label };
    }, [categories]);
    function breadcrumb(catId: number) {
        return (
            categories?.length
                ? getPathLabels(catId, parentIndex, labelIndex).join(" > ")
                : "")
    }
    useEffect(() => {
        filterParams.clientId = activeClientId;
        setSelectedPortfolioId(defaultPortfolioId);
        filterParams.portfolioId = defaultPortfolioId;
        refresh(defaultPortfolioId).catch(() => { });
        setBaseCurrency(currency);
        const total = filteredArtworks.reduce((sum: number, art: any) => {
            const rate = conversionRates[art.currencyCode] || 1;
            return sum + art.estimatedValue / rate;
        }, 0);
        setTotalValue(total);
    }, [activeClientId]);
    useEffect(() => {
        const fetchArtworks = async () => {
            setSelectedPortfolioId(filterParams.portfolioId ?? defaultPortfolioId);
            try {
                refresh(selectedPortfolioId).catch(() => { });
                setBaseCurrency(currency);
                const total = filteredArtworks.reduce((sum: number, art: any) => {
                    const rate = conversionRates[art.currencyCode] || 1;
                    return sum + art.estimatedValue / rate;
                }, 0);
                setTotalValue(total);
            } catch (err) {
                console.error("Error fetching artworks:", err);
                setError("Failed to load artworks.");
            }
        };
        fetchArtworks();
        if (categories) setCategoriesData(categories);
    }, [getAccessToken, currency, refreshTrigger, filterParams, selectedPortfolioId, valueViewOption]);

    // Expensive fetchArtworks on valueViewOption - to do - change to recalc with current data
    const artistLookup: Record<number, string> = Object.fromEntries(
        (artists ?? []).map(a => [a.artistId, a.artistName])
    );
    const categoryLookup: Record<number, string> = Object.fromEntries(
        (categoriesData ?? []).map(a => [a.id, a.name])
    );
    const safeLocations: InputLocation[] = Array.isArray(locations) ? locations : [];
    useEffect(() => {
        if (!valuationData || Object.keys(conversionRates).length === 0) return;
        setBaseValuationData(
            convertArtworkDetailsToBaseCurrency(valuationData, currency, conversionRates)
        );
    }, [valuationData, currency, conversionRates]);
    useEffect(() => {
        const filtered = artworks.filter(art =>
            matchesTextFilter(art, filterParams) &&
            matchesArtistFilter(art, filterParams) &&
            matchesCategoryFilter(art, filterParams) &&
            matchesLocationFilter(art, filterParams) &&
            matchesStatusFilter(art, filterParams) &&
            matchesDateFilter(art.dateAcquired, filterParams.acquiredFrom, filterParams.acquiredTo) &&
            matchesValuationFilter(art, filterParams, baseValuationData) &&
            matchesImageStatus(art, filterParams.imageStatus) &&
            matchesAcquisitionValueStatus(art, filterParams.acquisitionValueStatus) &&
            matchesInsuranceValueStatus(art, filterParams.insuranceValueStatus) &&
            matchesFairMarketValueStatus(art, filterParams.fairMarketValueStatus)
        );
        setFilteredArtworks(filtered);
        if (!filtered.length || Object.keys(conversionRates).length === 0) {
            setTotalValue(0);
            return;
        }
        const total = filtered.reduce((sum: number, art: any) => {
            const fromRate = conversionRates[art.currencyCode] || 1;
            const toRate = conversionRates[currency] || 1;
            const converted = (art.estimatedValue / fromRate) * toRate;
            return sum + converted;
        }, 0);
        setTotalValue(total);
    }, [artworks, filterParams, baseValuationData, conversionRates, currency]);
    // Helper functions
    //Asset Present
    function matchesImageStatus(art: ArtworkStub, filter: PresenceFilter | undefined) {
        if (!filter || filter === "any") return true;
        const hasImage =
            (art.imageCount > 0);
        if (filter === "with") return hasImage;
        if (filter === "without") return !hasImage;
        return true;
    }
    function matchesAcquisitionValueStatus(art: ArtworkStub, filter: PresenceFilter | undefined) {
        if (!filter || filter === "any") return true;
        const hasAcquisitionValue =
            art.acquisitionValuation?.lowerRangeAmount != null
        if (filter === "with") return hasAcquisitionValue;
        if (filter === "without") return !hasAcquisitionValue;
        return true;
    }
    function matchesInsuranceValueStatus(art: ArtworkStub, filter: PresenceFilter | undefined) {
        if (!filter || filter === "any") return true;
        const hasInsuranceValue =
            art.insuranceValuation?.lowerRangeAmount != null
        if (filter === "with") return hasInsuranceValue;
        if (filter === "without") return !hasInsuranceValue;
        return true;
    }
    function matchesFairMarketValueStatus(art: ArtworkStub, filter: PresenceFilter | undefined) {
        if (!filter || filter === "any") return true;
        const hasFairMarketValue =
            art.fairMarketValuation?.lowerRangeAmount != null;
        if (filter === "with") return hasFairMarketValue;
        if (filter === "without") return !hasFairMarketValue;
        return true;
    }
    // Text Filter: Title, Description, or Artist Name
    const matchesTextFilter = (artwork: ArtworkStub, params: SearchData) => {
        const query = (params.description ?? "").toLowerCase();
        if (query === "") return true;
        return artwork.artWorkTitle.toLowerCase().includes(query) ||
            artwork.artistName.toLowerCase().includes(query) ||
            (artwork.artDescription?.toLowerCase().includes(query) ?? false);
    };
    // Selected Artists Filter
    const matchesArtistFilter = (artwork: ArtworkStub, params: SearchData) => {
        if (params.artistIds === undefined || params.artistIds.length === 0)
            return true;
        return params.artistIds.includes(artwork.artistId);
    }
    // Category
    const matchesCategoryFilter = (artwork: ArtworkStub, params: SearchData) => {
        if (params.categoryIds === undefined || params.categoryIds.length === 0)
            return true;
        return params.categoryIds.includes(artwork.categoryId);
    }
    // Locations Filter
    const matchesLocationFilter = (artwork: ArtworkStub, params: SearchData) => {
        if (params.locations === undefined || params.locations.length === 0)
            return true
        return params.locations.includes(artwork.locationId);
    }
    // Artwork Status Filter (drives the Quick Filters menu)
    const matchesStatusFilter = (artwork: ArtworkStub, params: SearchData) => {
        if (!params.statusIds || params.statusIds.length === 0) return true;
        if (artwork.artworkStatusId == null) return false;
        return params.statusIds.includes(artwork.artworkStatusId);
    }
    // Valuation Range Filter
    const matchesValuationFilter = (artwork: ArtworkStub, params: SearchData, valuationData: ArtworkBaseValuation[] | null) => {
        if (!params.minValue && !params.maxValue) return true;
        if (!valuationData) return false;
        const valuationMap = new Map(valuationData.map(v => [v.artworkId, v]));
        const valuations = valuationMap.get(artwork.artworkId);
        const value = valuations?.[params.valuationType];
        if (value == null) return false;
        const min = Number(params.minValue) || -Infinity;
        const max = Number(params.maxValue) || Infinity;
        return value >= min && value <= max;
    };

    useEffect(() => {
        if (!filteredArtworks.length || Object.keys(conversionRates).length === 0) return;
        const total = filteredArtworks.reduce((sum: number, art: any) => {
            const fromRate = conversionRates[art.currencyCode] || 1;
            const toRate = conversionRates[currency] || 1;
            const converted = (art.estimatedValue / fromRate) * toRate;
            return sum + converted;
        }, 0);
        //Convert Valuations
        if (valuationData) {
            setBaseValuationData(convertArtworkDetailsToBaseCurrency(valuationData, currency, conversionRates));
        }
        setTotalValue(total);
    }, [conversionRates]);
    //Key Stats:
    const artistCount = React.useMemo(() => {
        return new Set(
            filteredArtworks
                .map(a => a.artistName?.trim())
                .filter(Boolean)
        ).size;
    }, [filteredArtworks]);
    const categoryCount = React.useMemo(() => {
        return new Set(
            filteredArtworks
                .map(a => a.categoryId)
                .filter(Boolean)
        ).size;
    }, [filteredArtworks]);
    const locationCount = React.useMemo(() => {
        return new Set(
            filteredArtworks
                .map(a => a.locationId)
                .filter(Boolean)
        ).size;
    }, [filteredArtworks]);
    const withImageCount = React.useMemo(() => {
        return new Set(
            filteredArtworks
                .map(a => a.imageUrls)
                .filter(Boolean)
        ).size;
    }, [filteredArtworks]);
    // GetSortValue uses valueViewOption from closure
    const GetSortValue = (art: ArtworkStub): number => {
        switch (valueViewOption) {
            case "acquisition":
                return art.acquisitionValuation?.lowerRangeAmount ?? 0;
            case "insurance":
                return art.insuranceValuation?.lowerRangeAmount ?? 0;
            case "fairMarketValue":
                return art.fairMarketValuation?.lowerRangeAmount ?? 0;
            default:
                return art.acquisitionValuation?.lowerRangeAmount ?? 0;
        }
    };
    const sortedArtworks = React.useMemo(() => {
        const items = [...filteredArtworks];
        items.sort((a, b) => {
            switch (sortBy) {
                case "artist":
                    return a.artistName.localeCompare(b.artistName);
                case "artist_desc":
                    return b.artistName.localeCompare(a.artistName);
                case "title":
                    return a.artWorkTitle.localeCompare(b.artWorkTitle);
                case "title_desc":
                    return b.artWorkTitle.localeCompare(a.artWorkTitle);
                case "category":
                    return a.categoryName.localeCompare(b.categoryName);
                case "category_desc":
                    return b.categoryName.localeCompare(a.categoryName);
                case "location":
                    return a.locationPath.localeCompare(b.locationPath);
                case "location_desc":
                    return b.locationPath.localeCompare(a.locationPath);
                case "acquired_desc":
                    return new Date(b.dateAcquired ?? 0).getTime() - new Date(a.dateAcquired ?? 0).getTime();
                case "acquired_asc":
                    return new Date(a.dateAcquired ?? 0).getTime() - new Date(b.dateAcquired ?? 0).getTime();
                case "value_desc":
                    return GetSortValue(b) - GetSortValue(a);
                case "value_asc":
                    return GetSortValue(a) - GetSortValue(b);
                default:
                    return 0;
            }
        });
        return items;
    }, [filteredArtworks, sortBy]);
    const [exportingExcel, setExportingExcel] = useState(false);
    // Mirrors the C# format string $"AMH-{id:000000}" — zero-padded to 6 digits.
    function formatArtworkReference(artworkId: number): string {
        return `AMH-${String(artworkId).padStart(6, "0")}`;
    }
    function buildArtworkExportFileName(): string {
        const scopeLabel = selectedPortfolioId
            ? portfolios.find((p) => p.portfolioId === selectedPortfolioId)?.name ?? `portfolio-${selectedPortfolioId}`
            : "full-collection";
        const safeScope = scopeLabel
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        const date = new Date().toISOString().slice(0, 10);
        return `artworks-${safeScope}-${date}.xlsx`;
    }

    // Excel Export
    async function mapWithConcurrency<T, R>(
        items: T[],
        limit: number,
        fn: (item: T, index: number) => Promise<R>
    ): Promise<R[]> {
        const results: R[] = new Array(items.length);
        let cursor = 0;
        async function worker() {
            while (cursor < items.length) {
                const i = cursor++;
                results[i] = await fn(items[i], i);
            }
        }
        await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
        return results;
    }

    // ExcelJS only embeds png / jpeg / gif

    // Convert fetched bytes to a base64 data URL — the reliable path for ExcelJS in the browser
    function arrayBufferToBase64(buffer: ArrayBuffer, mimeType: string): string {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return `data:${mimeType};base64,${btoa(binary)}`;
    }
    function resolveImageExtension(url: string, contentType?: string | null): "png" | "jpeg" | "gif" | null {
        const type = (contentType ?? "").toLowerCase();
        if (type.includes("jpeg") || type.includes("jpg")) return "jpeg";
        if (type.includes("png")) return "png";
        if (type.includes("gif")) return "gif";
        const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "jpeg";
        if (ext === "png") return "png";
        if (ext === "gif") return "gif";
        return null; // e.g. webp/svg — not supported by ExcelJS, will fall back to a link
    }

    async function fetchThumbnail(
        url?: string
    ): Promise<{ base64: string; extension: "png" | "jpeg" | "gif" } | null> {
        if (!url) return null;
        try {
            const res = await fetch(url);
            if (!res.ok) {
                console.warn(`Thumbnail fetch failed (${res.status}) for`, url);
                return null;
            }
            const contentType = res.headers.get("content-type");
            const extension = resolveImageExtension(url, contentType);
            if (!extension) {
                console.warn("Unsupported thumbnail format (need png/jpeg/gif), got:", contentType, url);
                return null;
            }
            const buffer = await res.arrayBuffer();
            const base64 = arrayBufferToBase64(buffer, `image/${extension}`);
            return { base64, extension };
        } catch (err) {
            console.warn("Thumbnail fetch threw (likely CORS) for", url, err);
            return null;
        }
    }

    function downloadWorkbook(buffer: ExcelJS.Buffer, filename: string) {
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    async function handleExportExcel() {
        try {
            setExportingExcel(true);

            //Refresh Artworks to ensure we have the latest data before exporting
            await refresh(selectedPortfolioId).catch(() => { });

            //const showValues = filterParams.showValues ?? false;

            const thumbnails = await mapWithConcurrency(sortedArtworks, 6, (art) =>
                fetchThumbnail((art as { imageUrl?: string }).imageUrl)
            );

            const workbook = new ExcelJS.Workbook();
            workbook.creator = "Art Management System";
            workbook.created = new Date();

            const worksheet = workbook.addWorksheet("Artworks", {
                views: [{ state: "frozen", ySplit: 1 }], // freeze header row
            });

            const baseColumns: Partial<ExcelJS.Column>[] = [
                { header: "Thumbnail", key: "thumbnail", width: 12 },
                { header: "Reference", key: "reference", width: 16 },
                { header: "Status", key: "status", width: 16 },
                { header: "Artist", key: "artist", width: 24 },
                { header: "Title", key: "title", width: 32 },
                { header: "Category", key: "category", width: 18 },
                { header: "Location", key: "location", width: 26 },
                { header: "Date Acquired", key: "dateAcquired", width: 16 },
            ];
            const valueColumns: Partial<ExcelJS.Column>[] = showValues
                ? [
                    { header: "Value", key: "value", width: 16 },
                    { header: "Value Currency", key: "valueCurrency", width: 14 },
                    { header: `Converted Value (${currency})`, key: "convertedValue", width: 20 },
                ]
                : [];
            worksheet.columns = [...baseColumns, ...valueColumns];

            // Header styling
            const headerRow = worksheet.getRow(1);
            headerRow.height = 22;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0D057D" } };
                cell.alignment = { vertical: "middle", horizontal: "center" };
                cell.border = { bottom: { style: "thin", color: { argb: "FF08034A" } } };
            });
            worksheet.autoFilter = {
                from: { row: 1, column: 1 },
                to: { row: 1, column: worksheet.columns.length },
            };

            sortedArtworks.forEach((art, i) => {
                let rowData: Record<string, string | number> = {
                    reference: formatArtworkReference(art.artworkId),
                    status: getArtworkStatusInfo(art.artworkStatusId)?.name ?? "",
                    artist: art.artistName,
                    title: art.artWorkTitle || "Untitled",
                    category: getCategoryNameById(art.categoryId) ?? art.categoryName ?? "",
                    location: art.locationPath ?? "",
                    dateAcquired: art.dateAcquired
                        ? new Date(art.dateAcquired).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                        })
                        : "",
                };

                if (showValues) {
                    let valueBasis = 0;
                    let currencyBasis = art.currencyCode ?? "USD";
                    switch (valueViewOption) {
                        case "fairMarketValue":
                            valueBasis = art.fairMarketValuation?.lowerRangeAmount ?? 0;
                            currencyBasis = art.fairMarketValuation?.currencyCode ?? art.currencyCode;
                            break;
                        case "insurance":
                            valueBasis = art.insuranceValuation?.lowerRangeAmount ?? 0;
                            currencyBasis = art.insuranceValuation?.currencyCode ?? art.currencyCode;
                            break;
                        default:
                            valueBasis = art.acquisitionValuation?.lowerRangeAmount ?? 0;
                            currencyBasis = art.acquisitionValuation?.currencyCode ?? art.currencyCode;
                            break;
                    }
                    const fromRate = conversionRates[currencyBasis] || 1;
                    const toRate = conversionRates[currency] || 1;
                    const convertedValue = (valueBasis / fromRate) * toRate;
                    rowData = {
                        ...rowData,
                        value: valueBasis,
                        valueCurrency: currencyBasis,
                        convertedValue: Number(convertedValue.toFixed(2)),
                    };
                }

                const row = worksheet.addRow(rowData);
                row.height = 48; // room for the thumbnail
                row.eachCell((cell, colNumber) => {
                    cell.border = { bottom: { style: "thin", color: { argb: "FFE0E0E0" } } };
                    if (colNumber === 1) return; // thumbnail column has no text
                    cell.alignment = { vertical: "middle", wrapText: colNumber === 5 || colNumber === 7 };
                });
                if (i % 2 === 1) {
                    row.eachCell((cell) => {
                        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F7FA" } };
                    });
                }
                if (showValues) {
                    ["value", "convertedValue"].forEach((key) => {
                        const cell = row.getCell(worksheet.getColumn(key).number);
                        cell.numFmt = "#,##0.00";
                        cell.alignment = { horizontal: "right", vertical: "middle" };
                    });
                }

                const thumb = thumbnails[i];
                if (thumb) {
                    const imageId = workbook.addImage({ base64: thumb.base64, extension: thumb.extension });
                    worksheet.addImage(imageId, {
                        tl: { col: 0.1, row: row.number - 1 + 0.1 },
                        ext: { width: 46, height: 46 },
                        editAs: "oneCell",
                    });
                }
            });

            const buffer = await workbook.xlsx.writeBuffer();
            downloadWorkbook(buffer, buildArtworkExportFileName());
        } catch (err) {
            console.error("Failed to export artworks to Excel", err);
            setError("Failed to export artworks to Excel");
        } finally {
            setExportingExcel(false);
        }
    }

    //Saved Views
    const [savedViewsDialogOpen, setSavedViewsDialogOpen] = useState<boolean>(false);
    const [saveViewDialogOpen, setSaveViewDialogOpen] = useState<boolean>(false);
    const [savedViews, setSavedViews] = useState<SavedArtworkViewListItemDto[]>([]);
    const [loadingSavedViews, setLoadingSavedViews] = useState<boolean>(false);
    const [savingView, setSavingView] = useState<boolean>(false);
    const [pendingSavedPortfolioId, setPendingSavedPortfolioId] = useState<number | null>(null);
    const [pendingSavedView, setPendingSavedView] = useState<LoadSavedArtworkViewResultDto | null>(null);
    const [selectedSavedViewId, setSelectedSavedViewId] = useState<number | string | null>("");
    const [selectedSavedViewClientId, setSelectedSavedViewClientId] = useState<number | null>(null);
    const [selectedSavedViewPortfolioId, setSelectedSavedViewPortfolioId] = useState<number | null>(null);
    const {
        getSavedArtworkViews,
        getSavedArtworkViewById,
        saveArtworkView,
        updateArtworkView,
        deleteSavedArtworkView
    } = useArtworkViewsApi();
    function clearActiveSavedView() {
        setSelectedSavedViewId(null);
        setSelectedSavedViewClientId(null);
        setSelectedSavedViewPortfolioId(null);
    }
    async function loadSavedViews() {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        setLoadingSavedViews(true);
        try {
            const views = await getSavedArtworkViews(accessToken);
            setSavedViews(views);
        } finally {
            setLoadingSavedViews(false);
        }
    }
    useEffect(() => {
        loadSavedViews();
    }, [savedViewsDialogOpen, user]);
    useEffect(() => {
        if (pendingSavedPortfolioId == null) return;
        const exists = portfolios.some(p => p.portfolioId === pendingSavedPortfolioId);
        setSelectedPortfolioId(exists ? pendingSavedPortfolioId : null);
        setPendingSavedPortfolioId(null);
    }, [pendingSavedPortfolioId, portfolios]);
    useEffect(() => {
        if (!pendingSavedView) return;
        const parsedSearchData = JSON.parse(pendingSavedView.searchDataJson) as SearchData;
        // clear dependent state first
        setSelectedPortfolioId(null);
        // apply non-dependent state
        setFilterParams(parsedSearchData);
        setSortBy(pendingSavedView.sortBy ?? "artist");
        setValueViewOption((pendingSavedView.valueViewOption as ValuationType) ?? "insurance");
        // trigger normal client change flow
        setActiveClientId(pendingSavedView.clientId ?? null);
    }, [pendingSavedView]);
    useEffect(() => {
        if (!pendingSavedView) return;
        if (activeClientId !== (pendingSavedView.clientId ?? null)) return;
        // only try once portfolios for the new client are available
        const targetPortfolioId = pendingSavedView.portfolioId ?? null;
        if (targetPortfolioId == null) {
            setSelectedPortfolioId(null);
            if (pendingSavedView.warningMessage) {
                console.warn(pendingSavedView.warningMessage);
                setError(pendingSavedView.warningMessage);
            }
            setPendingSavedView(null);
            return;
        }
        const exists = portfolios.some(p => p.portfolioId === targetPortfolioId);
        setSelectedPortfolioId(exists ? targetPortfolioId : null);
        if (pendingSavedView.warningMessage) {
            console.warn(pendingSavedView.warningMessage);
            setError(pendingSavedView.warningMessage);
        }
        setPendingSavedView(null);
    }, [pendingSavedView, activeClientId, portfolios]);
    async function handleLoadSavedView(id: number) {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        const result = await getSavedArtworkViewById(id, accessToken);
        const parsedSearchData = JSON.parse(result.searchDataJson) as SearchData;
        const targetClientId = parsedSearchData.portfolioId ?? null;
        const targetPortfolioId = parsedSearchData.portfolioId ?? null;
        setSelectedSavedViewId(id);
        setSelectedSavedViewClientId(targetClientId);
        setSelectedSavedViewPortfolioId(targetPortfolioId);
        setArtworks([]);
        setFilteredArtworks([]);
        setTotalValue(0);
        setPendingSavedView({
            ...result,
            searchDataJson: result.searchDataJson
        });
        setSavedViewsDialogOpen(false);
        return;
    }
    async function handleSaveView(name: string) {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        setSavingView(true);
        try {
            await saveArtworkView(
                {
                    name,
                    clientId: activeClientId ?? null,
                    portfolioId: selectedPortfolioId ?? null,
                    searchDataJson: JSON.stringify(filterParams),
                    sortBy,
                    valueViewOption
                },
                accessToken
            );
            setSaveViewDialogOpen(false);
            await loadSavedViews();
        } finally {
            setSavingView(false);
        }
    }
    async function handleUpdateSavedView(id: number) {
    }
    async function handleDeleteSavedView(id: number) {
        const accessToken = await getAccessToken();
        if (!accessToken) return;
        await deleteSavedArtworkView(id, accessToken);
        await loadSavedViews();
    }

    if (!artworks || !activeClientId) return (<div>Loading artwork...</div>);
    if (error) return <Alert severity="error">{error}</Alert>;
    return (
        <>
            <div style={{ padding: "10px" }}>
                <CollectionSwitcher
                    collections={user?.clients ?? []}
                    activeClientId={activeClientId}
                    onSelectClient={(clientId) => {
                        setActiveClientId(clientId);
                    }}
                    onEditClient={(clientId) => {
                        navigate(`/settings/clients?clientId=${clientId}`);
                    }}
                />
                <div className="info-cards">
                    <div className="info-card">
                        <SectionLabel htmlFor="portfolio-select">Collection Scope</SectionLabel>
                        <div className="inline-controls">
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: showValues ? "1fr 1fr" : "1fr" },
                                    gap: 1.5,
                                    width: "100%",
                                }}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{
                                        minWidth: 0,
                                        "& .MuiSelect-select": {
                                            py: { xs: 0.9, sm: 1 },
                                            fontSize: "0.9rem",
                                        },
                                    }}
                                >
                                    <InputLabel id="portfolio-select-label">Portfolio</InputLabel>
                                    <Select
                                        id="portfolio-select"
                                        labelId="portfolio-select-label"
                                        label="Portfolio"
                                        value={selectedPortfolioId}
                                        onChange={(e) => {
                                            const value = Number(e.target.value);
                                            handlePortfolioChange(value);
                                        }}
                                    >
                                        {hasClientLevelRole &&
                                            <MenuItem value={0} sx={{ fontSize: "0.9rem" }}>
                                                Full Collection
                                            </MenuItem>
                                        }
                                        {portfolios.map((p) => (
                                            <MenuItem
                                                key={p.portfolioId}
                                                value={p.portfolioId}
                                                sx={{ fontSize: "0.9rem" }}
                                            >
                                                {p.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {showValues && (
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        sx={{
                                            minWidth: 0,
                                            "& .MuiSelect-select": {
                                                py: { xs: 0.9, sm: 1 },
                                                fontSize: "0.9rem",
                                            },
                                        }}
                                    >
                                        <InputLabel id="value-basis-label">Value Basis</InputLabel>
                                        <Select
                                            labelId="value-basis-label"
                                            label="Value Basis"
                                            value={valueViewOption}
                                            onChange={(e) =>
                                                setValueViewOption(e.target.value as ValuationType)
                                            }
                                        >
                                            <MenuItem value="acquisition">Acquisition</MenuItem>
                                            <MenuItem value="insurance">Insurance</MenuItem>
                                            <MenuItem value="fairMarketValue">Fair Market Value</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        </div>
                    </div>
                    <div className="info-card">
                        <SectionLabel htmlFor="saved-view-select">Saved View</SectionLabel>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.25
                            }}
                        >
                            {/* Saved View Selector */}
                            <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                    "& .MuiSelect-select": {
                                        py: 1,
                                        fontSize: "0.9rem"
                                    }
                                }}
                            >
                                <InputLabel id="saved-view-select-label">Saved View</InputLabel>
                                <Select
                                    labelId="saved-view-select-label"
                                    id="saved-view-select"
                                    label="Saved View"
                                    value={selectedSavedViewId ?? ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value) {
                                            handleLoadSavedView(Number(value));
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select saved view</em>
                                    </MenuItem>
                                    {savedViews.map(view => (
                                        <MenuItem key={view.id} value={view.id}>
                                            {view.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {/* Active View Indicator */}
                            {/*{selectedSavedViewId && (*/}
                            {/*    <Typography*/}
                            {/*        variant="caption"*/}
                            {/*        sx={{*/}
                            {/*            color: "text.secondary",*/}
                            {/*            pl: 0.25*/}
                            {/*        }}*/}
                            {/*    >*/}
                            {/*        Active view:{" "}*/}
                            {/*        <strong>*/}
                            {/*            {savedViews.find(v => v.id === selectedSavedViewId)?.name}*/}
                            {/*        </strong>*/}
                            {/*    </Typography>*/}
                            {/*)}*/}
                            {/* Buttons */}
                            <Stack direction="column" spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSavedViewsDialogOpen(true)}
                                >
                                    Manage
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setSaveViewDialogOpen(true)}
                                >
                                    Save Current
                                </Button>
                            </Stack>
                        </Box>
                    </div>
                    <div className="info-card">
                        <SectionLabel htmlFor="Collection">Portfolio Stats</SectionLabel>
                        <div className="hover-preview-meta">
                            <HoverMetaRow label="Items" value={filteredArtworks.length} />
                            <HoverMetaRow label="With Images" value={withImageCount} />
                            <HoverMetaRow label="Artists" value={artistCount} />
                            <HoverMetaRow label="Categories" value={categoryCount} />
                            <HoverMetaRow label="Locations" value={locationCount} />

                        </div>
                    </div>
                    {/*<div className="info-card">*/}
                    {/*    <SectionLabel htmlFor="Exchange Rates">{currency} Exchange Rates</SectionLabel>*/}
                    {/*    <CurrencyTickerTape*/}
                    {/*        baseCurrency={baseCurrency}*/}
                    {/*        precision={4}*/}
                    {/*        speedPxPerSec={90}*/}
                    {/*        order="alpha"*/}
                    {/*        pollMs={180_000} // optional: refresh rate - 3 minutes*/}
                    {/*    />*/}
                    {/*</div>*/}
                    {hasFinanceRole && (
                        <div className="info-card info-card--wide">
                            <SectionLabel htmlFor="Portfolio Value">Portfolio Value</SectionLabel>
                            <PortfolioValuation
                                baseCurrency={currency}
                                clientId={activeClientId}
                                conversionRates={conversionRates}
                                portfolioId={selectedPortfolioId}
                            />
                        </div>
                    )}
                </div>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ mb: 1.5 }}>
                        Portfolio Items
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", md: "center" },
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 1.5,
                                alignItems: { xs: "stretch", sm: "center" },
                            }}
                        >
                            {viewMode === "card" && (
                                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
                                    <Select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            bgcolor: "background.paper",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <MenuItem value="artist">Sort by Artist</MenuItem>
                                        <MenuItem value="title">Sort by Title</MenuItem>
                                        <MenuItem value="category">Sort by Category</MenuItem>
                                        <MenuItem value="location">Sort by Location</MenuItem>
                                        <MenuItem value="acquired_desc">Newest Acquired</MenuItem>
                                        <MenuItem value="acquired_asc">Oldest Acquired</MenuItem>
                                        <MenuItem value="value_desc">Highest Value</MenuItem>
                                        <MenuItem value="value_asc">Lowest Value</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            <QuickFilterMenu
                                filterParams={filterParams}
                                onChange={setFilterParams}
                                hasFinanceRole={hasFinanceRole}
                                valueViewOption={valueViewOption}
                            />
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                size="small"
                                onChange={(e, mode) => setViewMode(mode)}
                                aria-label="Gallery display mode"
                            >
                                <ToggleButton value="card" aria-label="Card view">
                                    <Tooltip title="Card view">
                                        <GridViewIcon fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="table" aria-label="Table view">
                                    <Tooltip title="Table view">
                                        <ViewListIcon fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: { xs: "stretch", md: "flex-end" },
                                }}
                            >
                                <Tooltip title="Export current items to Excel">
                                    <span>
                                        <Button
                                            variant="outlined"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={handleExportExcel}
                                            disabled={filteredArtworks.length === 0 || exportingExcel}
                                        >
                                            Export to Excel
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Generate a report for current items">
                                    <span>
                                        <Button
                                            variant="contained"
                                            startIcon={<AssessmentIcon />}
                                            onClick={handleReportPreparation}
                                            disabled={filteredArtworks.length === 0}
                                        >
                                            Generate Report
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
               
                    </Box>
                </Box>
                <ActiveFiltersBar
                    filters={filterParams}
                    onChange={setFilterParams}
                    artistLookup={artistLookup}
                    categoryLookup={categoryLookup}
                    locationLookup={locationLookup}
                />
                {viewMode === "table" ? (
                    <ArtworkTableView
                        artworks={sortedArtworks}
                        showValues={showValues ?? false}
                        valueViewOption={valueViewOption}
                        currency={currency}
                        conversionRates={conversionRates}
                        sortBy={sortBy}
                        onColumnSort={handleColumnSort}
                        breadcrumb={breadcrumb}
                        getCategoryNameById={getCategoryNameById}
                    />
                ) : (
                    <div className={`gallery-grid ${compactView ? "compact" : ""}`}>
                        {sortedArtworks.map((art, index) => {
                            const acquisitionValue = art.acquisitionValuation?.lowerRangeAmount ?? 0;
                            const acquisitionCurrency = art.acquisitionValuation?.currencyCode ?? art.currencyCode;
                            const insuranceValue = art.insuranceValuation?.lowerRangeAmount ?? 0;
                            const insuranceCurrency = art.insuranceValuation?.currencyCode ?? art.currencyCode;
                            const fmvValue = art.fairMarketValuation?.lowerRangeAmount ?? 0;
                            const fmvCurrency = art.fairMarketValuation?.currencyCode ?? art.currencyCode;
                            var valueBasis = 0;
                            var currencyBasis = "USD";
                            switch (valueViewOption) {
                                case "fairMarketValue":
                                    valueBasis = fmvValue;
                                    currencyBasis = fmvCurrency;
                                    break;
                                case "insurance":
                                    valueBasis = insuranceValue;
                                    currencyBasis = insuranceCurrency;
                                    break;
                                default:
                                    valueBasis = acquisitionValue;
                                    currencyBasis = acquisitionCurrency;
                                    break;
                            }
                            const fromRate = conversionRates[currencyBasis] || 1;
                            const toRate = conversionRates[currency] || 1;
                            const convertedValue = (valueBasis / fromRate) * toRate;
                            const imageSrc = art.imageUrl || "";
                            const imageCount = art.imageCount ?? 0;
                            const acquiredDate = art.dateAcquired
                                ? new Date(art.dateAcquired).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                })
                                : null;
                            return (
                                <div
                                    key={art.artworkId}
                                    className={`catalogue-card ${compactView ? "compact" : ""}`}
                                    onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const viewportWidth = window.innerWidth;
                                        const viewportHeight = window.innerHeight;
                                        const spaceOnRight = viewportWidth - rect.right;
                                        const spaceOnLeft = rect.left;
                                        let x =
                                            spaceOnRight >= HOVER_CARD_WIDTH + HOVER_CARD_GAP
                                                ? rect.right + HOVER_CARD_GAP
                                                : rect.left - HOVER_CARD_WIDTH - HOVER_CARD_GAP;
                                        if (x < 12) x = 12;
                                        if (x + HOVER_CARD_WIDTH > viewportWidth - 12) {
                                            x = viewportWidth - HOVER_CARD_WIDTH - 12;
                                        }
                                        let y = rect.top;
                                        const maxY = viewportHeight - HOVER_CARD_HEIGHT_ESTIMATE - 12;
                                        if (y > maxY) y = Math.max(12, maxY);
                                        setHoverArtwork(art);
                                        setHoverPosition({ x, y });
                                    }}
                                    onMouseLeave={() => {
                                        setHoverArtwork(null);
                                        setHoverPosition(null);
                                    }}
                                >
                                    <Link
                                        to={`/artwork/${art.artworkId}`}
                                        id={`artwork-${art.artworkId}`}
                                        state={{
                                            source: 'gallery',
                                            artworks: sortedArtworks,
                                            currentIndex: index,
                                        } satisfies ArtworkNavigationState}
                                        style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                        <div className="catalogue-image-frame">
                                            <ArtworkStatusBadge statusId={art.artworkStatusId} />
                                            {imageSrc ? (
                                                <img
                                                    src={imageSrc}
                                                    alt={art.artWorkTitle}
                                                    className="catalogue-image"
                                                    onLoad={(e) => e.currentTarget.classList.add("loaded")}
                                                />
                                            ) : (
                                                <div className="catalogue-image-placeholder">
                                                    <div className="no-images">
                                                        <div className="no-images-icon">🖼️</div>
                                                        {!compactView && <p>No image available</p>}
                                                    </div>
                                                </div>
                                            )}
                                            {imageCount > 1 && (
                                                <div className="catalogue-image-count">
                                                    {imageCount} images
                                                </div>
                                            )}
                                        </div>
                                        <div className="catalogue-body">
                                            <p className="catalogue-artist" title={art.artistName}>
                                                {art.artistName}
                                            </p>
                                            <div
                                                className={`catalogue-title ${compactView ? "compact" : ""}`}
                                                title={art.artWorkTitle}
                                                style={compactView ? { WebkitLineClamp: 1 } : { WebkitLineClamp: 2 }}
                                            >
                                                {art.artWorkTitle || "Untitled"}
                                            </div>
                                            <div className="catalogue-meta">
                                                {!compactView && art.medium && (
                                                    <div className="catalogue-medium" title={art.medium}>
                                                        {art.medium}
                                                    </div>
                                                )}

                                                <p
                                                    className="catalogue-meta-line"
                                                    title={compactView ? art.categoryName : breadcrumb(art.categoryId)}
                                                >
                                                    {compactView
                                                        ? <span className="art-category-pill">
                                                            {getCategoryNameById(art.categoryId)}
                                                        </span>
                                                        : breadcrumb(art.categoryId)}
                                                </p>

                                                {!compactView && (
                                                    <>
                                                        <p className="catalogue-meta-line" title={art.locationPath}>
                                                            {art.locationPath}
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                            {showValues && (
                                                <div className="catalogue-valuation">
                                                    <p className="catalogue-valuation-primary">
                                                        {valueBasis.toLocaleString()} {currencyBasis}
                                                    </p>
                                                    <p className="catalogue-valuation-secondary">
                                                        Approx. {convertedValue.toLocaleString(undefined, {
                                                            maximumFractionDigits: 2,
                                                        })} {currency}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {hoverArtwork && hoverPosition && !isMobile && (
                <ArtworkHoverPreview
                    artwork={hoverArtwork}
                    position={hoverPosition}
                    currency={currency}
                    showValues={showValues ?? false}
                    convertedValue={
                        ((hoverArtwork.acquisitionValuation?.lowerRangeAmount ?? 0) / (conversionRates[hoverArtwork.acquisitionValuation?.currencyCode ?? currency] || 1)) *
                        (conversionRates[currency] || 1)
                    }
                />
            )}
            <ReportSelectionDialog
                open={showReportDialog}
                onClose={handleReportDialogClose}
                onGenerate={handleGenerateReport}
                currentSearch={filterSummary}
                //defaultTitle={defaultReportTitle}
                generating={generatingReport}
            />
            <SavedArtworkViewsDialog
                open={savedViewsDialogOpen}
                onClose={() => setSavedViewsDialogOpen(false)}
                views={savedViews}
                loading={loadingSavedViews}
                onLoad={handleLoadSavedView}
                onDelete={handleDeleteSavedView}
            />
            <SaveArtworkViewDialog
                open={saveViewDialogOpen}
                onClose={() => setSaveViewDialogOpen(false)}
                onSave={handleSaveView}
                saving={savingView}
                currentScopeText={
                    activeClientId
                        ? `Client ${activeClientId}${selectedPortfolioId ? ` · Portfolio ${selectedPortfolioId}` : " · Full Collection"}`
                        : undefined
                }
            />
        </>
    );
};
export default GalleryPage;
